<template>
	<div>
		<error-list :errors="page.errors"></error-list>
		<b-toast id="changes-saved" variant="success" solid title="Changes saved" toaster="b-toaster-top-center">
			Your changes have been saved.
		</b-toast>

		<b-form @submit.prevent="save">
			<b-row>
				<b-col md>
					<b-form-group label="Work Order #" :invalid-feedback="requiredFeedback($v.item.workOrderNumber)">
						<b-form-input type="text" required
									  v-model.trim="$v.item.workOrderNumber.$model" :state="getValidState($v.item.workOrderNumber)" />
					</b-form-group>

					<b-form-group label="Name" :invalid-feedback="requiredFeedback($v.item.name)">
						<b-form-input type="text"
									  v-model.trim="$v.item.name.$model" :state="getValidState($v.item.name)" />
					</b-form-group>

					<b-form-group label="Execution Date" :invalid-feedback="requiredFeedback($v.item.executionDate)">
						<b-form-input type="date" v-model.trim="$v.item.executionDate.$model" :state="getValidState($v.item.executionDate)"></b-form-input>
					</b-form-group>

					<b-form-group label="Contractor" :invalid-feedback="requiredFeedback($v.item.contractor)">
						<b-typeahead v-model.trim="$v.item.contractor.$model"
									 :data="options.contractor"
									 :max-matches="100" :minMatchingChars="1">
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Primary Point of Contact" :invalid-feedback="requiredFeedback($v.item.pocPrimary)">
						<b-typeahead v-model.trim="$v.item.pocPrimary.$model"
									 :data="options.pocPrimary"
									 :max-matches="100" :minMatchingChars="1">
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Secondary Point of Contact" :invalid-feedback="requiredFeedback($v.item.pocSecondary)">
						<b-typeahead v-model.trim="$v.item.pocSecondary.$model"
									 :data="options.pocSecondary"
									 :max-matches="100" :minMatchingChars="1">
						</b-typeahead>
					</b-form-group>
				</b-col>
				<b-col md>
					<b-form-group label="Funding Source" :invalid-feedback="requiredFeedback($v.item.fundingSourceId)">
						<b-form-select :options="setOptions.fundingSources"
							v-model.trim="$v.item.fundingSourceId.$model" :state="getValidState($v.item.fundingSourceId)">
							<b-form-select-option :value="null">-- Select a funding source --</b-form-select-option>	
						</b-form-select>
					</b-form-group>

					<b-form-group label="Project Type" :invalid-feedback="requiredFeedback($v.item.projectType)">
						<b-typeahead v-model.trim="$v.item.projectType.$model"
									 :data="setOptions.projectTypes"
									 :minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Project Subtype" :invalid-feedback="requiredFeedback($v.item.projectSubtype)">
						<b-typeahead v-model.trim="$v.item.projectSubtype.$model"
									 :data="setOptions.projectSubtypes"
									 :minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Completion Date" :invalid-feedback="requiredFeedback($v.item.completionDate)">
						<b-form-input type="date" v-model.trim="$v.item.completionDate.$model" :state="getValidState($v.item.completionDate)"></b-form-input>
					</b-form-group>
				</b-col>
			</b-row>

			<b-form-group label="Notes" :invalid-feedback="requiredFeedback($v.item.notes)" class="mb-4">
				<b-form-textarea v-model.trim="$v.item.notes.$model" :state="getValidState($v.item.notes)" rows="5"></b-form-textarea>
			</b-form-group>

			<div v-if="!isUpdate">
				<h3 class="h5 border-bottom my-4">Initial Revision Data</h3>

				<b-row>
					<b-col md>
						<b-form-group label="Revision #">
							<b-form-input type="number" v-model.trim="$v.revision.revisionNumber.$model" readonly></b-form-input>
						</b-form-group>

						<b-form-group label="Revision Date" :invalid-feedback="requiredFeedback($v.revision.revisionDate)">
							<b-form-input type="date" v-model.trim="$v.revision.revisionDate.$model" :state="getValidState($v.revision.revisionDate)"></b-form-input>
						</b-form-group>
					</b-col>
					<b-col md>
						<b-form-group label="Expiration Date" :invalid-feedback="requiredFeedback($v.revision.expirationDate)">
							<b-form-input type="date" v-model.trim="$v.revision.expirationDate.$model" :state="getValidState($v.revision.expirationDate)"></b-form-input>
						</b-form-group>
						<b-form-group label="Revision Notes" :invalid-feedback="requiredFeedback($v.revision.notes)" class="mb-4">
							<b-form-textarea v-model.trim="$v.revision.notes.$model" :state="getValidState($v.revision.notes)" rows="5"></b-form-textarea>
						</b-form-group>
					</b-col>
				</b-row>
			</div>

			<fixed-action-bar>
				<save-button :saving="page.saving" class="mr-2" />
				<back-button />
				<div v-if="$v.$invalid && $v.$anyDirty" class="ml-auto my-0 py-1 alert alert-warning">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width class="mr-1 text-danger" />
					You have errors in the form.
				</div>
			</fixed-action-bar>
		</b-form>

		<b-modal v-model="page.saveConfirmShow" title="Confirm Save" scrollable size="lg">
			<error-list :errors="page.errors"></error-list>

			<b-form-group label="Add a note to describe changes?" description="Optional">
				<b-form-input type="text" maxLength="100" v-model="page.saveDescription" />
			</b-form-group>

			<div slot="modal-footer">
				<save-button :saving="page.saving" class="mr-2" type="button" @click.native="confirmSave" />
				<b-button type="button" variant="primary" @click="page.saveConfirmShow = false" class="ml-1">Cancel</b-button>
			</div>
		</b-modal>
	</div>
</template>

<script>
	import { required, integer } from 'vuelidate/lib/validators';
	import _ from 'underscore';

	export default {
		name: 'SrWorkOrderForm',
		props: {
			isUpdate: {
				type: Boolean,
				default: false
			},
			item: {
				type: Object,
				required: true
			},
			setOptions: {
				type: Object,
				default: () => {
					return {
						fundingTypes: [],
						projectTypes: [],
						projectSubtypes: [],
						fundingSources: []
					}
				}
			}
		},
		data() {
			return {
				apiUrl: 'srworkorders',
				apiUrl2: 'srworkorderrevisions',
				editRoute: 'SrWorkOrderEdit',
				page: {
					errors: [],
					validated: false,
					saving: false,
					saveConfirmShow: false,
					saveDescription: null
				},
				options: {
					contractor: [],
					pocPrimary: [],
					pocSecondary: []
				},
				revision: {
					workOrderId: 0,
					revisionNumber: 0,
					revisionDate: null,
					expirationDate: null,
					notes: null,
				}
			}
		},
		computed: {
			itemContractor() {
				return this.item.contractor;
			},
			itemPocPrimary() {
				return this.item.pocPrimary;
			},
			itemPocSecondary() {
				return this.item.pocSecondary;
			},
		},
		validations: {
			item: {
				workOrderNumber: { required },
				name: {},
				executionDate: {},
				contractor: {},
				pocPrimary: {},
				pocSecondary: {},
				fundingSourceId: {},
				projectType: {},
				projectSubtype: {},
				notes: {},
				completionDate: {}
			},
			revision: {
				workOrderId: {},
				revisionNumber: { required, integer },
				revisionDate: {},
				expirationDate: {},
				notes: {},
			}
		},
		watch: {
			itemContractor: _.debounce(function (query) { this.find('contractor', query) }, 500),
			itemPocPrimary: _.debounce(function (query) { this.find('pocPrimary', query) }, 500),
			itemPocSecondary: _.debounce(function (query) { this.find('pocSecondary', query) }, 500)
		},
		created() {
			this.get();
		},
		methods: {
			get() {
				if (this.isUpdate) {
					this.item.executionDate = this.dateForForm(this.item.executionDate);
					this.item.completionDate = this.dateForForm(this.item.completionDate);
				}
			},
			async save() {
				this.log(this.item);
				this.page.errors = [];
				this.page.saving = true;

				if (this.$v.$invalid) {
					this.page.errors.push('Please fix the errors below and try again.');
				} else {
					if (this.isUpdate) {
						this.page.saveConfirmShow = true;
					} else {
						try {
							const response = await this.$http.post(this.apiUrl, this.item, this.getTokenHeader());
							this.log(response.data);

							let newId = response.data;
							this.revision.workOrderId = newId;
							const response2 = await this.$http.post(this.apiUrl2, this.revision, this.getTokenHeader());
							this.log(response2.data);

							this.$router.push({ name: this.editRoute, params: { id: newId } });
						} catch (error) {
							if (this.isApiUnauthorized(error)) this.page.showLogin = true;
							else this.page.errors = this.logError(error);
						}
					}
				}

				this.page.saving = false;
			},
			async confirmSave() {
				this.page.errors = [];
				this.page.saving = true;

				if (this.isUpdate) {
					try {
						let data = {
							item1: this.item,
							item2: this.isNullOrEmpty(this.page.saveDescription) ? "Changes saved." : this.page.saveDescription
						};
						const response = await this.$http.put(`${this.apiUrl}/${this.item.id}`, data, this.getTokenHeader());
						this.log(response.data);

						this.page.saveConfirmShow = false;
						this.$bvToast.show('changes-saved');
						this.$emit('change');
					} catch (error) {
						if (this.isApiUnauthorized(error)) this.page.showLogin = true;
						else this.page.errors = this.logError(error);
					}
				}

				this.page.saving = false;
			},
			launchToast() {
				this.$bvToast.show('changes-saved');
			},
			logValidations() {
				this.log(this.$v);
			},
			async find(field, query) {
				try {
					const response = await this.$http.get(`/${this.apiUrl}/find/${field}/${query}`, this.getTokenHeader());
					this.options[field] = response.data != '' ? response.data : [];
				} catch (error) {
					this.page.errors = this.logError(error);
				}
			},
		}
	}
</script>
